// Pure formatting helpers + constants shared between StrategyPricingModal.jsx
// (the live "just generated"/current-property view) and
// StrategyResultViewerModal.jsx (the read-only "View" for a previous
// property). Both need to render the exact same Pricing Strategy `result`
// shape identically - these used to live only inside StrategyPricingModal.jsx,
// which is why the read-only viewer drifted into a much plainer, unformatted
// rendering of the same data over time. Pulled out here so there is exactly
// one implementation of "what a comp/impact/confidence label looks like" for
// both surfaces to share.

import React from "react";
import { Icon, Popup } from "semantic-ui-react";
import { format } from "date-fns";

export function formatSoldDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : format(date, "MMM d, yyyy");
}

export function parseDollarNumber(value) {
  if (typeof value === "number") return value;
  const parsed = parseFloat(String(value || "").replace(/[^0-9.]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
}

// Normalizes a dollar amount to "$X,XXX,XXX" regardless of how it was
// entered (e.g. "390000", "$390,000", "390k" won't parse and is returned
// as-is rather than mangled - see parseDollarNumber). Falls back to the raw
// value when it isn't a parseable number, so a free-text entry is never
// dropped or garbled.
export function formatDollar(value) {
  if (!value) return null;
  const parsed = parseDollarNumber(value);
  return parsed !== null ? `$${Math.round(parsed).toLocaleString()}` : value;
}

// Deterministic (never AI-computed) percent/dollar difference between two
// dollar values - used for "X% below current asking price" and "original
// list price reduction" in the Buyer offer decision summary. Returns null
// when either value isn't a parseable number, rather than showing a
// misleading 0%/blank.
export function computePercentDifference(fromValue, toValue) {
  const from = parseDollarNumber(fromValue);
  const to = parseDollarNumber(toValue);
  if (from === null || to === null || from === 0) return null;
  const dollarDiff = to - from;
  return { dollarDiff: Math.round(dollarDiff), percentDiff: Math.round((dollarDiff / from) * 1000) / 10 };
}

// "Condition impact not evaluated." is the exact placeholder string
// buildPricingStrategyPrompt (functions/index.js) forces onto
// conditionImpact.summary whenever no condition/updates/known-issues info
// was given - the one reliable signal that condition genuinely wasn't
// evaluated, used to trigger the Buyer offer decision summary's early
// "Condition not confirmed" warning (see PricingStrategyResultBody.jsx).
export function isConditionUnknown(conditionImpact) {
  return !conditionImpact || conditionImpact.summary === "Condition impact not evaluated.";
}

// Key Concerns for the Buyer Strategies "Compare Homes" table
// (BuyerStrategyPropertyRows.jsx) - NEVER invented. Every entry here traces
// back to one of exactly three sources: (1) a deterministic comparison of
// numbers already on the result (asking price vs. estimated market value -
// pure arithmetic, not a guess), (2) text the agent actually typed into the
// Edit Details form (knownIssues/locationCons - verbatim, never
// paraphrased), or (3) a negative-impact keyFactor the AI already produced
// under generatePricingStrategy's strict "never invent a property
// characteristic that wasn't given" rule (the same factors already shown to
// the agent in that property's own "Why This Offer?" cards - reused here at
// the identical trust level, not a new claim). Returns [] (never a guessed
// placeholder) when none of these produced anything.
export function extractKeyConcerns(result) {
  if (!result) return [];
  const concerns = [];
  const asking = parseDollarNumber(result?.inputs?.currentPrice);
  const valueHigh = parseDollarNumber(result?.estimatedValueHigh);
  if (asking && valueHigh && asking > valueHigh) {
    concerns.push("Priced above estimated market value");
  }
  if (result?.inputs?.knownIssues) concerns.push(result.inputs.knownIssues);
  if (result?.inputs?.locationCons) concerns.push(result.inputs.locationCons);
  (result?.keyFactors || [])
    .filter((f) => f?.impact === "negative" && f?.summary)
    .forEach((f) => concerns.push(f.summary));
  return concerns;
}

// Canonical short chip tags for the Compare Homes "Key Concerns" column -
// property-specific only. Each rule only picks a SHORT LABEL for something
// extractKeyConcerns already established as true (verbatim agent text, an
// AI negative keyFactor already shown elsewhere, or the deterministic
// price-vs-value flag) - it never adds a new claim. A concern that matches
// no known pattern still shows up, just trimmed to chip length, rather than
// being silently dropped.
const CONCERN_TAG_RULES = [
  { pattern: /market value|overpriced|priced above/, tag: "Above value range", icon: "exclamation circle", color: "red" },
  { pattern: /foundation|structural/, tag: "Structural concerns", icon: "wrench", color: "red" },
  { pattern: /\broof\b/, tag: "Roof concerns", icon: "wrench", color: "red" },
  { pattern: /siding/, tag: "Siding replacement", icon: "wrench", color: "red" },
  { pattern: /water heater/, tag: "Old water heater", icon: "tint", color: "blue" },
  { pattern: /hvac|furnace|air condition/, tag: "HVAC concerns", icon: "tint", color: "blue" },
  { pattern: /plumbing|\bleak/, tag: "Plumbing concerns", icon: "tint", color: "blue" },
  { pattern: /electrical|wiring/, tag: "Electrical concerns", icon: "bolt", color: "red" },
  { pattern: /dated|outdated/, tag: "Dated finishes", icon: "info circle", color: "orange" },
  { pattern: /\bbusy\b|\btraffic\b|highway/, tag: "Busy road", icon: "volume up", color: "orange" },
  { pattern: /airport|flight path|airplane/, tag: "Airplane path", icon: "plane", color: "orange" },
  { pattern: /\bnoise\b/, tag: "Noise concerns", icon: "volume up", color: "orange" },
];

// A concern about broad market/financing backdrop (mortgage rates, general
// buyer demand) rather than the property itself - excluded from Key
// Concerns, which stays property-specific only. Belongs in Negotiation
// Signals/market context instead.
const NON_PROPERTY_SPECIFIC_PATTERN = /mortgage rate|interest rate|30-year fixed|financing cost|buyer demand/;

export function tagifyConcerns(concerns) {
  const tags = [];
  (concerns || []).forEach((concern) => {
    if (!concern) return;
    const lower = concern.toLowerCase();
    if (NON_PROPERTY_SPECIFIC_PATTERN.test(lower)) return;
    const matches = CONCERN_TAG_RULES.filter((r) => r.pattern.test(lower));
    if (matches.length > 0) {
      matches.forEach((m) => tags.push({ text: m.tag, icon: m.icon, color: m.color }));
    } else {
      const trimmed = concern.length > 28 ? `${concern.slice(0, 25).trim()}…` : concern;
      tags.push({ text: trimmed, icon: "warning circle", color: "red" });
    }
  });
  const seen = new Set();
  return tags.filter((t) => {
    if (seen.has(t.text)) return false;
    seen.add(t.text);
    return true;
  });
}

// "X% below/above asking" + the Target Purchase Range (falling back to the
// older expectedNegotiationLow/High field name for generations saved before
// targetPurchaseRangeLow/High existed - see PricingStrategyResultBody.jsx).
// Purely deterministic, mirrors the same computation used on the full
// Strategy result page so the two can never disagree.
export function buildNegotiatingPosition(result) {
  const lines = [];
  const askingDiff = computePercentDifference(result?.inputs?.currentPrice, result?.recommendedListPrice);
  if (askingDiff) {
    lines.push(`${Math.abs(askingDiff.percentDiff).toFixed(1)}% ${askingDiff.percentDiff <= 0 ? "below" : "above"} asking`);
  }
  const targetLow = result?.targetPurchaseRangeLow || result?.expectedNegotiationLow;
  const targetHigh = result?.targetPurchaseRangeHigh || result?.expectedNegotiationHigh;
  if (targetLow && targetHigh) lines.push(`Target: ${targetLow} – ${targetHigh}`);
  return lines;
}

export function formatPricePerSqft(salePrice, squareFeet) {
  const price = parseDollarNumber(salePrice);
  const sqft = parseDollarNumber(squareFeet);
  if (!price || !sqft) return null;
  return `$${Math.round(price / sqft)}/sqft`;
}

export const IMPACT_ICON = {
  positive: { name: "arrow up", color: "green" },
  negative: { name: "arrow down", color: "red" },
  neutral: { name: "exchange", color: "grey" },
  buyer_dependent: { name: "users", color: "blue" },
};

export const IMPACT_EXPLANATION = {
  positive: "Favors the subject.",
  negative: "Favors the comp.",
  neutral: "Roughly a wash between the two.",
  buyer_dependent: "Not universally better or worse - different buyers will weigh this differently.",
};

export function ImpactIcon({ impact }) {
  const config = IMPACT_ICON[impact] || IMPACT_ICON.neutral;
  return (
    <Popup
      trigger={<Icon name={config.name} color={config.color} />}
      content={IMPACT_EXPLANATION[impact] || IMPACT_EXPLANATION.neutral}
      position="top center"
      size="small"
    />
  );
}

export const COMP_STRENGTH_COLOR = {
  Strong: "green",
  Moderate: "yellow",
  "Context Only": "grey",
};

export const COMP_STRENGTH_EXPLANATION = {
  Strong: "Closely matches the subject in size, condition, and location - a reliable data point for pricing.",
  Moderate: "Relevant, but differs from the subject in one or more meaningful ways (e.g. size, bed/bath count, or condition) - should inform the price, not solely determine it.",
  "Context Only": "Useful background, but not directly comparable (e.g. very different size, location, or property type) - shouldn't be used to justify a specific price point.",
};

// Direct vs. Expanded comparable-sale buckets (comparableSales[].analyticalRole,
// set deterministically in functions/index.js's buildPricingStrategyPrompt /
// generatePricingStrategy - never AI-decided). Older saved generations
// predate this field and simply won't have it; PricingStrategyResultBody.jsx
// falls back to the original Strong/Moderate grouping when it's absent.
export const ANALYTICAL_ROLE_LABEL = {
  direct: "Direct Comparable Sales",
  expanded: "Expanded Comparable Sales",
};

export const SOURCE_TIER_LABEL = {
  unverified: "Unverified source",
};

export const VALUATION_ROLE_LABEL = {
  supports_lower: "Supports Lower Range",
  supports_core: "Supports Core Price",
  supports_upper: "Supports Upper Range",
  context_only: "Context Only",
};

export const VALUATION_ROLE_COLOR = {
  supports_lower: "blue",
  supports_core: "green",
  supports_upper: "purple",
  context_only: "grey",
};

export const VALUATION_WEIGHT_LABEL = {
  high: "High Weight",
  medium: "Medium Weight",
  low: "Low Weight",
  none: "Not Weighted",
};

// Real estate site names the research/comp prompts are allowed to report,
// mapped to a domain for building a search link. We never fabricate a direct
// listing URL - instead this builds a Google site-search link that reliably
// finds the actual page.
export const KNOWN_REAL_ESTATE_SITE_DOMAINS = {
  Zillow: "zillow.com",
  Redfin: "redfin.com",
  "Realtor.com": "realtor.com",
  Compass: "compass.com",
  Trulia: "trulia.com",
  "Homes.com": "homes.com",
  Movoto: "movoto.com",
};

export function buildSiteSearchUrl(siteName, address) {
  const domain = KNOWN_REAL_ESTATE_SITE_DOMAINS[siteName];
  if (!domain || !address) return null;
  return `https://www.google.com/search?q=${encodeURIComponent(`site:${domain} ${address}`)}`;
}

// Occupancy status options for the Buyer-only "Edit Details" form and the
// "Seller's Potential Motivations" card - agent-entered only (see
// StrategyPricingModal.jsx), never auto-filled/inferred, since no available
// data source can reliably distinguish vacant from tenant-occupied without
// risking a fabricated claim.
export const HOME_STATUS_OPTIONS = [
  { key: "vacant", text: "Vacant", value: "vacant" },
  { key: "owner_occupied", text: "Owner Occupied", value: "owner_occupied" },
  { key: "tenant_occupied", text: "Tenant Occupied (Rental)", value: "tenant_occupied" },
  { key: "unknown", text: "Unknown", value: "unknown" },
];

export const HOME_STATUS_LABEL = {
  vacant: "Vacant",
  owner_occupied: "Owner Occupied",
  tenant_occupied: "Tenant Occupied",
  unknown: "Unknown",
};

export const CONFIDENCE_COLOR = {
  High: "green",
  "Moderate-High": "olive",
  Moderate: "yellow",
  "Low-Moderate": "orange",
  Low: "red",
};

export function buildDynamicDisclaimer(sourcesUsed) {
  const sources = sourcesUsed || [];
  if (sources.includes("MLS") && sources.includes("Datafiniti (Public Record)")) {
    return "This analysis uses structural details from the live MLS import for this property, public record comp data, and agent-provided information. Verify market conditions and property details before finalizing a pricing recommendation.";
  }
  if (sources.includes("MLS")) {
    return "This analysis uses structural property details from the live MLS import for this property, plus agent-provided information. It does not include live MLS comp data - verify against current local comparable sales before finalizing.";
  }
  if (sources.includes("Datafiniti (Public Record)")) {
    return "This analysis uses available public record data and agent-provided information. This is not a live MLS feed - verify market conditions and property details before finalizing a pricing recommendation.";
  }
  if (sources.includes("Web Search")) {
    return "This analysis includes information gathered from a live web search plus agent-provided details. It does not include live MLS data - verify against current local comparable sales before finalizing.";
  }
  if (sources.includes("Agent Provided")) {
    return "This analysis uses property and comparable-sale information provided by the agent. Verify all information before relying on the recommendation.";
  }
  return "This is an AI-generated starting point based on general market knowledge and does not include live MLS data or verified comparable sales. Verify against current local comparable sales before finalizing.";
}
